import React, { useState } from 'react';
import {
  useRequestPhoneChangeMutation,
  useVerifyPhoneChangeMutation,
} from '@/redux/internal-services/usersApi';
import OtpCodeInput from '@/components/UI/OtpCodeInput';
import { Button } from '@/components/UI/Button';

interface PhoneVerifyStepProps {
  /** The phone number the code was sent to. */
  phone: string;
  /** Called after the code is confirmed and the phone is verified. */
  onVerified: () => void;
  onBack: () => void;
}

/**
 * Dedicated verification screen shown after the user enters an unverified phone number on the
 * profile step. Mirrors the email verification card so onboarding reads as one consistent flow;
 * the code is delivered via SMS (phone-change API).
 */
const PhoneVerifyStep: React.FC<PhoneVerifyStepProps> = ({ phone, onVerified, onBack }) => {
  const [verifyPhoneChange, { isLoading: verifying }] = useVerifyPhoneChangeMutation();
  const [requestPhoneChange, { isLoading: resending }] = useRequestPhoneChangeMutation();
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    try {
      await verifyPhoneChange({ otp: otpCode.trim() }).unwrap();
      onVerified();
    } catch {
      setOtpError('Invalid or expired code. Please try again.');
    }
  };

  const sendOtp = async () => {
    setOtpError('');
    try {
      await requestPhoneChange({ new_phone: phone }).unwrap();
    } catch {
      setOtpError('Failed to send verification code.');
    }
  };

  return (
    <div className="card-surface px-6 py-8 text-center sm:px-10 sm:py-10">
      <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center mx-auto mb-6">
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-accent-text" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z" />
        </svg>
      </div>

      <h2 className="text-h2 text-ui-text mb-2">Verify Your Phone</h2>
      <p className="text-ui-text2 text-sm mb-1">We sent a verification code to:</p>
      <p className="font-semibold text-ui-text text-sm mb-6">{phone}</p>

      <form onSubmit={handleVerifyOtp}>
        <p className="text-sm font-medium text-ui-text text-left mb-3">Enter 6-Digit Code</p>
        <div className="mb-6">
          <OtpCodeInput value={otpCode} onChange={setOtpCode} autoFocus />
        </div>

        {otpError && <p className="text-danger-text text-sm mb-4">{otpError}</p>}

        <Button type="submit"
          disabled={verifying || otpCode.length !== 6}
          size="lg" className="w-full">
          {verifying ? 'Verifying...' : 'Verify & Continue'}
        </Button>
      </form>

      <p className="mt-5 text-sm text-ui-text2">
        Didn't receive the code?{' '}
        <button
          type="button"
          onClick={sendOtp}
          disabled={resending}
          className="text-accent-text font-medium hover:underline disabled:opacity-50"
        >
          {resending ? 'Sending...' : 'Resend'}
        </button>
      </p>

      <button
        type="button"
        onClick={onBack}
        className="mt-4 text-sm text-ui-text2 hover:underline"
      >
        Back
      </button>
    </div>
  );
};

export default PhoneVerifyStep;
